// Pure puzzle rules, shared by the game and puzzle-bank checks.
export function conflicts(a, b, size, regions) {
  const ar = Math.floor(a / size);
  const br = Math.floor(b / size);
  const ac = a % size;
  const bc = b % size;
  return (
    ar === br ||
    ac === bc ||
    regions[a] === regions[b] ||
    (Math.abs(ar - br) <= 1 && Math.abs(ac - bc) <= 1)
  );
}

export function blockedCells(puzzle, cats) {
  const occupied = new Set(cats);
  const blocked = new Set();
  for (let index = 0; index < puzzle.regions.length; index += 1) {
    if (
      !occupied.has(index) &&
      cats.some((cat) => conflicts(index, cat, puzzle.size, puzzle.regions))
    ) {
      blocked.add(index);
    }
  }
  return blocked;
}

export function candidates(puzzle, cats, marks = []) {
  const unavailable = new Set([
    ...cats,
    ...marks,
    ...blockedCells(puzzle, cats),
  ]);
  return puzzle.regions
    .map((_, index) => index)
    .filter((index) => !unavailable.has(index));
}

// One cat per row means diagonal contact only needs checking in neighboring rows.
// MRV (fewest available cells first) keeps solution enumeration small.
export function solvePuzzle(regions, size, { givens = [], limit = 2 } = {}) {
  if (regions.length !== size * size || new Set(regions).size !== size)
    return [];
  const fixed = new Map();
  for (const index of givens) {
    if (!Number.isInteger(index) || index < 0 || index >= regions.length)
      return [];
    const row = Math.floor(index / size);
    if (fixed.has(row) && fixed.get(row) !== index % size) return [];
    fixed.set(row, index % size);
  }
  if (
    givens.some((a, i) =>
      givens.slice(i + 1).some((b) => conflicts(a, b, size, regions)),
    )
  )
    return [];

  const placed = Array(size).fill(-1);
  const solutions = [];
  const usedColumns = new Set();
  const usedRegions = new Set();

  function choicesFor(row) {
    const columns = fixed.has(row)
      ? [fixed.get(row)]
      : Array.from({ length: size }, (_, i) => i);
    return columns.filter((column) => {
      if (
        usedColumns.has(column) ||
        usedRegions.has(regions[row * size + column])
      )
        return false;
      if (
        row > 0 &&
        placed[row - 1] >= 0 &&
        Math.abs(placed[row - 1] - column) <= 1
      )
        return false;
      if (
        row + 1 < size &&
        placed[row + 1] >= 0 &&
        Math.abs(placed[row + 1] - column) <= 1
      )
        return false;
      return true;
    });
  }

  function visit(depth) {
    if (solutions.length >= limit) return;
    if (depth === size) {
      solutions.push(placed.map((column, row) => row * size + column));
      return;
    }
    let nextRow = -1;
    let choices = [];
    for (let row = 0; row < size; row += 1) {
      if (placed[row] >= 0) continue;
      const available = choicesFor(row);
      if (!available.length) return;
      if (nextRow < 0 || available.length < choices.length) {
        nextRow = row;
        choices = available;
      }
    }
    for (const column of choices) {
      const region = regions[nextRow * size + column];
      placed[nextRow] = column;
      usedColumns.add(column);
      usedRegions.add(region);
      visit(depth + 1);
      placed[nextRow] = -1;
      usedColumns.delete(column);
      usedRegions.delete(region);
      if (solutions.length >= limit) return;
    }
  }
  visit(0);
  return solutions;
}

export function isSolved(puzzle, cats) {
  if (cats.length !== puzzle.size || new Set(cats).size !== cats.length)
    return false;
  if (
    cats.some(
      (index) =>
        !Number.isInteger(index) || index < 0 || index >= puzzle.size ** 2,
    )
  )
    return false;
  return !cats.some((a, i) =>
    cats.slice(i + 1).some((b) => conflicts(a, b, puzzle.size, puzzle.regions)),
  );
}

export function placementIssue(puzzle, cats, index) {
  const size = puzzle.size;
  if (cats.some((cat) => Math.floor(cat / size) === Math.floor(index / size)))
    return "This row already has a cat. Each row needs just one.";
  if (cats.some((cat) => cat % size === index % size))
    return "This column already has a cat. Give this one a different column.";
  if (cats.some((cat) => puzzle.regions[cat] === puzzle.regions[index]))
    return "This colored region already has its cat.";
  if (cats.some((cat) => conflicts(cat, index, size, puzzle.regions)))
    return "A little too close! Cats cannot touch, even at the corners.";
  if (!puzzle.solution.includes(index))
    return "This spot would leave another cat without a home. Try ruling out a few more cells.";
  return null;
}

export function nextHint(puzzle, cats, marks) {
  const crossedHome = puzzle.solution.find(
    (index) => marks.includes(index) && !cats.includes(index),
  );
  if (crossedHome !== undefined) {
    return {
      type: "note",
      index: crossedHome,
      message: `Take another look at row ${Math.floor(crossedHome / puzzle.size) + 1}, column ${(crossedHome % puzzle.size) + 1}. This home was crossed out too soon; that note has been cleared.`,
    };
  }
  const available = candidates(puzzle, cats, marks);
  const groups = [];
  for (let i = 0; i < puzzle.size; i += 1) {
    groups.push({
      name: `Row ${i + 1}`,
      cells: available.filter((index) => Math.floor(index / puzzle.size) === i),
    });
    groups.push({
      name: `Column ${i + 1}`,
      cells: available.filter((index) => index % puzzle.size === i),
    });
    groups.push({
      name: `Region ${String.fromCharCode(65 + i)}`,
      cells: available.filter((index) => puzzle.regions[index] === i),
    });
  }
  const forced = groups.find(
    (group) =>
      group.cells.length === 1 && puzzle.solution.includes(group.cells[0]),
  );
  if (forced)
    return {
      type: "cat",
      index: forced.cells[0],
      message: `${forced.name} has only one possible home left. That cat belongs right here.`,
    };
  const unsolved = groups
    .filter((group) => group.cells.length > 0)
    .sort((a, b) => a.cells.length - b.cells.length);
  for (const group of unsolved) {
    const index = group.cells.find(
      (cell) => puzzle.solution.includes(cell) && !cats.includes(cell),
    );
    if (index !== undefined)
      return {
        type: "cat",
        index,
        message:
          "Looking a few steps ahead, this is the home that lets every other cat fit. One little nudge to keep you going.",
      };
  }
  return null;
}

/** Cross help never uncovers a cat or changes a player's existing notes. */
export function nextCrossHint(puzzle, cats, marks, { skipBlocked = false } = {}) {
  const blocked = blockedCells(puzzle, cats);
  const available = puzzle.regions
    .map((_, index) => index)
    .filter(
      (index) =>
        !cats.includes(index) &&
        !marks.includes(index) &&
        !puzzle.solution.includes(index) &&
        !(skipBlocked && blocked.has(index)),
    );
  available.sort((a, b) => Number(blocked.has(b)) - Number(blocked.has(a)));
  const indices = available.slice(0, 3);
  if (!indices.length) return null;
  return {
    indices,
    message: indices.every((index) => blocked.has(index))
      ? "These cells share a row, column, or color with a cat, or touch one. They've been crossed out."
      : "These cells can be ruled out so every cat still has a home. They've been crossed out.",
  };
}

export function hashSeed(value) {
  let hash = 2166136261;
  for (const character of String(value))
    hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
  return hash >>> 0;
}

export function randomFrom(seed) {
  let value = typeof seed === "number" ? seed >>> 0 : hashSeed(seed);
  return () => {
    value += 0x6d2b79f5;
    let result = Math.imul(value ^ (value >>> 15), value | 1);
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61);
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296;
  };
}

export function transformPuzzle(base, variant = 0) {
  const { size } = base;
  const turns = variant % 4;
  const mirrored = Math.floor(variant / 4) % 2 === 1;
  function transform(index) {
    let row = Math.floor(index / size);
    let column = index % size;
    if (mirrored) column = size - 1 - column;
    for (let turn = 0; turn < turns; turn += 1)
      [row, column] = [column, size - 1 - row];
    return row * size + column;
  }
  const regions = Array(size ** 2);
  base.regions.forEach((region, index) => {
    regions[transform(index)] = region;
  });
  return {
    ...base,
    variant,
    regions,
    solution: base.solution.map(transform).sort((a, b) => a - b),
    givens: (base.givens ?? []).map(transform),
  };
}

export function dailyKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
